import React, { useEffect, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { TrendingUp, Loader2, Lock, AlertTriangle } from 'lucide-react';
import { supabase } from '../../lib/supabaseClient';
import { useAuth } from '../../context/AuthContext';

/**
 * Dedicated page for the password-reset flow. The user lands here from the link
 * in their reset email; Supabase (detectSessionInUrl) consumes the recovery
 * token and fires PASSWORD_RECOVERY, which AuthContext flags as a recovery-only
 * session. Here we let them set a new password, then clear the flag and send
 * them into the app.
 *
 * A recovery session is deliberately kept out of the app (RequireAuth/AuthPage
 * redirect it back here) until the password is actually updated.
 */
export function ResetPasswordPage() {
  const { session, loading: authLoading, isRecoverySession, clearRecovery } = useAuth();

  const [pw1, setPw1] = useState('');
  const [pw2, setPw2] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  // Always-dark, matching AuthPage/LandingPage: pin <html> to dark while mounted,
  // then restore the previous class on unmount so the in-app theme is untouched.
  useEffect(() => {
    const html = document.documentElement;
    const wasLight = html.classList.contains('light');
    if (wasLight) {
      html.classList.remove('light');
      html.classList.add('dark');
    }
    return () => {
      if (wasLight) {
        html.classList.remove('dark');
        html.classList.add('light');
      }
    };
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (pw1.length < 6) {
      setError('Password must be at least 6 characters.');
      return;
    }
    if (pw1 !== pw2) {
      setError('Passwords do not match.');
      return;
    }

    setSubmitting(true);
    try {
      const { error: updateError } = await supabase.auth.updateUser({ password: pw1 });
      if (updateError) {
        setError(updateError.message);
        return;
      }
      // Password is set — the session is now a normal one. Clear the recovery
      // flag so the guards let the user into the app.
      clearRecovery();
      setDone(true);
    } catch {
      setError('Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // Success → into the app (recovery flag already cleared above).
  if (done) {
    return <Navigate to="/app" replace />;
  }

  // A normal (non-recovery) session has no reset in progress — nothing to do here.
  if (!authLoading && session && !isRecoverySession) {
    return <Navigate to="/app" replace />;
  }

  const hasRecovery = !!session && isRecoverySession;

  let content: React.ReactNode;
  if (authLoading) {
    content = (
      <div className="flex items-center justify-center py-6">
        <Loader2 className="w-6 h-6 animate-spin text-slate-500" />
      </div>
    );
  } else if (hasRecovery) {
    content = (
      <>
        <h1 className="font-display text-xl font-bold text-slate-100">Set a new password</h1>
        <p className="text-sm text-slate-400 mt-1">Choose a new password for your account.</p>

        <form onSubmit={handleSubmit} className="mt-6 space-y-4">
          <div>
            <label className="label-caps block mb-1.5" htmlFor="reset-pw1">New password</label>
            <input
              id="reset-pw1"
              type="password"
              autoComplete="new-password"
              required
              minLength={6}
              value={pw1}
              onChange={(e) => setPw1(e.target.value)}
              className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2.5 text-sm text-slate-100 placeholder:text-slate-500 outline-none focus:border-[var(--color-bronze-bright)] transition-colors"
              placeholder="••••••••"
            />
          </div>

          <div>
            <label className="label-caps block mb-1.5" htmlFor="reset-pw2">Confirm new password</label>
            <input
              id="reset-pw2"
              type="password"
              autoComplete="new-password"
              required
              minLength={6}
              value={pw2}
              onChange={(e) => setPw2(e.target.value)}
              className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2.5 text-sm text-slate-100 placeholder:text-slate-500 outline-none focus:border-[var(--color-bronze-bright)] transition-colors"
              placeholder="••••••••"
            />
          </div>

          {error && (
            <div className="text-xs rounded-lg px-3 py-2 bg-[var(--color-negative)]/10 border border-[var(--color-negative)]/30 text-negative">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={submitting}
            className="w-full flex items-center justify-center gap-2 rounded-lg px-4 py-2.5 text-sm font-medium transition-transform hover:scale-[1.01] disabled:opacity-60 disabled:hover:scale-100"
            style={{ backgroundColor: 'var(--color-bronze-bright)', color: '#0c0e12' }}
          >
            {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
            Update password
          </button>
        </form>
      </>
    );
  } else {
    // No recovery session — the link was invalid, already used, or expired.
    content = (
      <>
        <h1 className="font-display text-xl font-bold text-slate-100">Reset link invalid</h1>
        <p className="text-sm text-slate-400 mt-1">
          This password reset link is invalid or has expired.
        </p>

        <div className="mt-6 text-xs rounded-lg px-3 py-2.5 bg-[var(--color-negative)]/10 border border-[var(--color-negative)]/30 text-negative flex items-start gap-2">
          <AlertTriangle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
          <span>Reset links can only be used once and expire after a short time. Request a fresh one and try again.</span>
        </div>

        <Link
          to="/login?mode=forgot"
          className="mt-6 w-full flex items-center justify-center gap-2 rounded-lg px-4 py-2.5 text-sm font-medium transition-transform hover:scale-[1.01]"
          style={{ backgroundColor: 'var(--color-bronze-bright)', color: '#0c0e12' }}
        >
          Request a new link
        </Link>
      </>
    );
  }

  return (
    <div className="min-h-screen bg-slate-900 flex flex-col items-center justify-center px-4 py-12">
      {/* Brand */}
      <Link to="/" className="flex items-center gap-2 mb-8">
        <div className="p-2 bg-[rgb(var(--overlay)/0.06)] rounded-xl border border-slate-700">
          <TrendingUp className="w-5 h-5 text-slate-400" />
        </div>
        <div>
          <span className="font-display text-lg font-bold text-slate-100">MyCrypto</span>
          <span className="font-display text-lg font-bold text-gradient">Planner</span>
        </div>
      </Link>

      <div className="w-full max-w-sm bg-slate-950 border border-slate-700 rounded-2xl p-8">
        {content}

        <div className="mt-6 text-center text-sm text-slate-400">
          <Link to="/login" className="text-bronze-bright hover:underline inline-flex items-center gap-1">
            <Lock className="w-3.5 h-3.5" />
            Back to log in
          </Link>
        </div>
      </div>

      <Link to="/" className="mt-6 text-xs text-slate-500 hover:text-slate-300 transition-colors">
        ← Back to home
      </Link>
    </div>
  );
}
